import { isValidElement } from 'react';
import { hasTemplate } from '../interpolation/hasTemplate';
import { isPlainArrayOrObject } from '../interpolation/isPlainArrayOrObject';

export const INTERNAL_BINDING_KEY_PREFIXES = [
  '_bind.',
  '_action.',
  '_meta.',
] as const;

export function isInternalBindingPropKey(key: string): boolean {
  for (const prefix of INTERNAL_BINDING_KEY_PREFIXES) {
    if (key.startsWith(prefix)) {
      return true;
    }
  }
  return false;
}

export function isPuckComponentNode(value: unknown): value is {
  type: string;
  props: Record<string, unknown>;
} {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) {
    return false;
  }
  const node = value as Record<string, unknown>;
  return (
    typeof node.type === 'string' &&
    node.props !== null &&
    typeof node.props === 'object' &&
    !Array.isArray(node.props)
  );
}

/**
 * A Puck node or an already-built React element. Either one resolves its own
 * bindings when it renders, inside its own item scope. React elements are
 * plain objects, so without this check the walk would read an element's
 * `props` as prop data: it strips the child's `_bind.*` keys and fills its
 * `{{item.*}}` templates against the parent's scope, where there may be no
 * item at all.
 */
function isRenderableNode(value: unknown): boolean {
  return isPuckComponentNode(value) || isValidElement(value);
}

function shouldSkipContainer(value: unknown, stack: WeakSet<object>): boolean {
  if (value === null || typeof value !== 'object') {
    return true;
  }
  if (isRenderableNode(value)) {
    return true;
  }
  if (!isPlainArrayOrObject(value)) {
    return true;
  }
  return stack.has(value);
}

/** Returns true when any non-skipped string leaf contains a template. */
export function somePlainPropTemplate(
  value: unknown,
  stack: WeakSet<object>,
): boolean {
  if (typeof value === 'string') {
    return hasTemplate(value);
  }
  if (shouldSkipContainer(value, stack)) {
    return false;
  }

  stack.add(value as object);
  try {
    if (Array.isArray(value)) {
      for (const item of value) {
        if (somePlainPropTemplate(item, stack)) {
          return true;
        }
      }
      return false;
    }

    for (const [key, nested] of Object.entries(
      value as Record<string, unknown>,
    )) {
      if (isInternalBindingPropKey(key)) {
        continue;
      }
      if (somePlainPropTemplate(nested, stack)) {
        return true;
      }
    }
    return false;
  } finally {
    stack.delete(value as object);
  }
}

/** Invokes `fn` for every non-skipped string leaf. */
export function forEachPlainPropTemplateString(
  value: unknown,
  stack: WeakSet<object>,
  fn: (template: string) => void,
): void {
  if (typeof value === 'string') {
    fn(value);
    return;
  }
  if (shouldSkipContainer(value, stack)) {
    return;
  }

  stack.add(value as object);
  try {
    if (Array.isArray(value)) {
      for (const item of value) {
        forEachPlainPropTemplateString(item, stack, fn);
      }
      return;
    }

    for (const [key, nested] of Object.entries(
      value as Record<string, unknown>,
    )) {
      if (isInternalBindingPropKey(key)) {
        continue;
      }
      forEachPlainPropTemplateString(nested, stack, fn);
    }
  } finally {
    stack.delete(value as object);
  }
}

/** Maps string leaves and recurses plain arrays/objects; strips internal keys. */
export function mapPlainPropTree(
  value: unknown,
  stack: WeakSet<object>,
  mapString: (value: string, leafKey?: string) => unknown,
): unknown {
  if (typeof value === 'string') {
    return mapString(value, undefined);
  }
  if (value === null || typeof value !== 'object') {
    return value;
  }
  if (isRenderableNode(value)) {
    return value;
  }
  if (!isPlainArrayOrObject(value)) {
    return value;
  }
  if (stack.has(value)) {
    return value;
  }

  stack.add(value);
  try {
    if (Array.isArray(value)) {
      return mapPlainPropArray(value, stack, mapString);
    }
    return mapPlainPropObject(
      value as Record<string, unknown>,
      stack,
      mapString,
    );
  } finally {
    stack.delete(value);
  }
}

function mapPlainPropArray(
  input: ReadonlyArray<unknown>,
  stack: WeakSet<object>,
  mapString: (value: string, leafKey?: string) => unknown,
): ReadonlyArray<unknown> {
  let out: unknown[] | null = null;
  for (let i = 0; i < input.length; i++) {
    const original = input[i];
    const next = isRenderableNode(original)
      ? original
      : mapPlainPropTree(original, stack, mapString);
    if (next !== original && out === null) {
      out = input.slice(0, i);
    }
    if (out !== null) {
      out.push(next);
    }
  }
  return out ?? input;
}

function mapPlainPropObject(
  input: Record<string, unknown>,
  stack: WeakSet<object>,
  mapString: (value: string, leafKey?: string) => unknown,
): Record<string, unknown> {
  let out: Record<string, unknown> | null = null;
  let strippedInternalKey = false;
  const keys = Object.keys(input);

  for (let i = 0; i < keys.length; i++) {
    const k = keys[i];
    if (isInternalBindingPropKey(k)) {
      strippedInternalKey = true;
      continue;
    }

    const original = input[k];
    const next = mapPlainPropTree(original, stack, (value, leafKey) =>
      mapString(value, leafKey ?? k),
    );

    if (next !== original && out === null) {
      out = {};
      for (let j = 0; j < i; j++) {
        const prevKey = keys[j];
        if (!isInternalBindingPropKey(prevKey)) {
          out[prevKey] = input[prevKey];
        }
      }
    }
    if (out !== null) {
      out[k] = next;
    }
  }

  if (out !== null) {
    return out;
  }
  if (strippedInternalKey) {
    const stripped: Record<string, unknown> = {};
    for (const k of keys) {
      if (!isInternalBindingPropKey(k)) {
        stripped[k] = input[k];
      }
    }
    return stripped;
  }
  return input;
}
